import { FieldAddon } from '@linktr.ee/arbor/components/Field/Addon';
import { FieldControl } from '@linktr.ee/arbor/components/Field/Control';
import { FieldInput } from '@linktr.ee/arbor/components/Field/Input';
import { FieldLabel } from '@linktr.ee/arbor/components/Field/Label';
import { FieldMessage } from '@linktr.ee/arbor/components/Field/Message';
import { FieldRoot } from '@linktr.ee/arbor/components/Field/Root';
import { cn } from '@linktr.ee/arbor/utils/cn';
import {
  type FocusEvent,
  forwardRef,
  type InputHTMLAttributes,
  memo,
  type ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';

export type InputTextProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'id' | 'disabled'
> & {
  className?: string;
  state?: 'disabled' | 'default' | 'error';
  name?: string;
  id?: string;
  label: string;
  showEditButton?: boolean;
  value?: string;
  prefix?: ReactNode;
  icon?: ReactNode;
  errorMessage?: ReactNode;
  disabled?: boolean;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onEdit?: () => void;
};

export const InputText = memo(
  forwardRef<HTMLInputElement, InputTextProps>(
    (
      {
        className,
        id,
        name = 'input',
        state = 'default',
        label,
        showEditButton = false,
        disabled,
        required,
        value = '',
        prefix,
        icon,
        errorMessage,
        onEdit,
        type = 'text',
        ...props
      },
      ref
    ) => {
      const { t } = useTranslation();
      const fieldId = useMemo(() => id || name, [id, name]);
      const fieldState = disabled || state === 'disabled' ? 'disabled' : state;
      const showEditButtonNow =
        showEditButton && value.length > 0 && fieldState === 'disabled';

      const handleEdit = useCallback(() => onEdit?.(), [onEdit]);

      return (
        <FieldRoot
          id={fieldId}
          state={fieldState}
          className="w-full"
          data-testid="input-text-root"
        >
          <FieldControl
            className="overflow-clip"
            data-testid="input-text-wrapper"
          >
            {prefix && <FieldAddon align="start">{prefix}</FieldAddon>}
            <FieldLabel data-testid="input-text-floating-label">
              {label}
            </FieldLabel>
            <FieldInput
              {...props}
              ref={ref}
              name={name}
              type={type}
              value={value}
              required={required}
              aria-required={required}
              className={cn(className)}
              data-testid="input-text-input"
              data-state={fieldState}
            />
            {showEditButtonNow ? (
              <FieldAddon align="end">
                <button
                  type="button"
                  onClick={handleEdit}
                  className="text-body-small-medium text-primary transition-colors hover:text-accent focus:outline-none"
                  data-testid="input-edit-button"
                >
                  {t('Edit')}
                </button>
              </FieldAddon>
            ) : (
              icon && <FieldAddon align="end">{icon}</FieldAddon>
            )}
          </FieldControl>
          {errorMessage && <FieldMessage>{errorMessage}</FieldMessage>}
        </FieldRoot>
      );
    }
  )
);
InputText.displayName = 'InputText';
